/**
 * Onboarding gym step (native) — State → City → Gym progressive search.
 */
import { useState, useEffect, useCallback } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Modal,
  FlatList,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { MapPin, CheckCircle, ChevronLeft, X, Home, Hash, ChevronDown } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import OnboardingProgress from '@/components/OnboardingProgress';

const log = createLogger('ONBOARD_GYM');

const US_STATES = [
  {abbr:'AL',name:'Alabama'},{abbr:'AK',name:'Alaska'},{abbr:'AZ',name:'Arizona'},
  {abbr:'AR',name:'Arkansas'},{abbr:'CA',name:'California'},{abbr:'CO',name:'Colorado'},
  {abbr:'CT',name:'Connecticut'},{abbr:'DC',name:'District of Columbia'},
  {abbr:'DE',name:'Delaware'},{abbr:'FL',name:'Florida'},{abbr:'GA',name:'Georgia'},
  {abbr:'HI',name:'Hawaii'},{abbr:'ID',name:'Idaho'},{abbr:'IL',name:'Illinois'},
  {abbr:'IN',name:'Indiana'},{abbr:'IA',name:'Iowa'},{abbr:'KS',name:'Kansas'},
  {abbr:'KY',name:'Kentucky'},{abbr:'LA',name:'Louisiana'},{abbr:'ME',name:'Maine'},
  {abbr:'MD',name:'Maryland'},{abbr:'MA',name:'Massachusetts'},{abbr:'MI',name:'Michigan'},
  {abbr:'MN',name:'Minnesota'},{abbr:'MS',name:'Mississippi'},{abbr:'MO',name:'Missouri'},
  {abbr:'MT',name:'Montana'},{abbr:'NE',name:'Nebraska'},{abbr:'NV',name:'Nevada'},
  {abbr:'NH',name:'New Hampshire'},{abbr:'NJ',name:'New Jersey'},{abbr:'NM',name:'New Mexico'},
  {abbr:'NY',name:'New York'},{abbr:'NC',name:'North Carolina'},{abbr:'ND',name:'North Dakota'},
  {abbr:'OH',name:'Ohio'},{abbr:'OK',name:'Oklahoma'},{abbr:'OR',name:'Oregon'},
  {abbr:'PA',name:'Pennsylvania'},{abbr:'RI',name:'Rhode Island'},{abbr:'SC',name:'South Carolina'},
  {abbr:'SD',name:'South Dakota'},{abbr:'TN',name:'Tennessee'},{abbr:'TX',name:'Texas'},
  {abbr:'UT',name:'Utah'},{abbr:'VT',name:'Vermont'},{abbr:'VA',name:'Virginia'},
  {abbr:'WA',name:'Washington'},{abbr:'WV',name:'West Virginia'},{abbr:'WI',name:'Wisconsin'},
  {abbr:'WY',name:'Wyoming'},
];

type GymRow = { id: string; name: string; address: string; city: string; state: string; gym_code: string };

export default function OnboardingHomeGym() {
  const router = useRouter();
  const { user, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;
  const insets = useSafeAreaInsets();

  // Gym code tab
  const [gymCode, setGymCode] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [error, setError] = useState('');

  // Common
  const [selectedGym, setSelectedGym] = useState<{ id: string; name: string; address?: string; city?: string; state?: string; isHomeGym?: boolean } | null>(null);
  const [searchMode, setSearchMode] = useState<'search' | 'code'>('search');
  const [saving, setSaving] = useState(false);

  // Progressive search
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [stateModalOpen, setStateModalOpen] = useState(false);
  const [cityQuery, setCityQuery] = useState('');
  const [cityResults, setCityResults] = useState<string[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [gymQuery, setGymQuery] = useState('');
  const [gymResults, setGymResults] = useState<GymRow[]>([]);
  const [gymLoading, setGymLoading] = useState(false);

  // City autocomplete
  useEffect(() => {
    if (!selectedState || cityQuery.trim().length < 1) { setCityResults([]); return; }
    const t = setTimeout(async () => {
      setCityLoading(true);
      try {
        const { data } = await supabase
          .from('gyms')
          .select('city')
          .ilike('state', selectedState)
          .ilike('city', `%${cityQuery.trim()}%`)
          .order('city')
          .limit(100);
        const unique = [...new Set((data || []).map(r => r.city).filter(Boolean))].slice(0, 20);
        setCityResults(unique);
      } catch { setCityResults([]); }
      setCityLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [selectedState, cityQuery]);

  // Gym search
  useEffect(() => {
    if (!selectedState) { setGymResults([]); return; }
    if (!selectedCity && gymQuery.trim().length < 2) { setGymResults([]); return; }
    const t = setTimeout(async () => {
      setGymLoading(true);
      try {
        let query = supabase
          .from('gyms')
          .select('id, name, address, city, state, gym_code')
          .ilike('state', selectedState);
        if (selectedCity) query = query.ilike('city', selectedCity);
        if (gymQuery.trim().length >= 2) query = query.ilike('name', `%${gymQuery.trim()}%`);
        const { data } = await query.order('name').limit(30);
        setGymResults(data || []);
      } catch { setGymResults([]); }
      setGymLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [selectedState, selectedCity, gymQuery]);

  const lookupGymCode = async () => {
    const code = gymCode.trim().toUpperCase();
    if (code.length < 3) { setError('Enter a gym code (at least 3 characters)'); return; }
    setLookupLoading(true);
    setError('');
    try {
      const { data, error: dbError } = await supabase
        .from('gyms')
        .select('id, name, address, city, state')
        .ilike('gym_code', code)
        .limit(1)
        .maybeSingle();
      if (dbError) {
        setError('Something went wrong. Try again.');
      } else if (!data) {
        setError('No gym found with that code. Check with your gym or skip for now.');
      } else {
        setSelectedGym({ id: data.id, name: data.name, address: data.address ?? undefined, city: data.city, state: data.state });
        setError('');
      }
    } catch {
      setError('Something went wrong. Try again.');
    }
    setLookupLoading(false);
  };

  const clearAll = () => {
    setSelectedGym(null);
    setGymCode('');
    setError('');
    setSelectedState(null);
    setSelectedCity(null);
    setCityQuery('');
    setGymQuery('');
    setCityResults([]);
    setGymResults([]);
  };

  const saveGymAndContinue = async () => {
    setSaving(true);
    try {
      if (user?.id) {
        let gymTableId: string | null = null;
        if (selectedGym?.isHomeGym) {
          const { data: newGym, error: gymError } = await supabase.from('gyms')
            .insert({ name: 'My Home Gym', address: 'Home Gym', lat: 0, lng: 0, is_home_gym: true, created_by: user.id })
            .select('id')
            .single();
          if (gymError) log.warn('Failed to create home gym:', gymError);
          if (newGym) gymTableId = newGym.id;
        } else if (selectedGym && selectedGym.id !== 'pending') {
          gymTableId = selectedGym.id;
        }
        if (gymTableId) {
          const gymDisplayName = selectedGym?.name ?? null;
          const { error: gymUpdateError } = await supabase.from('profiles')
            .update({
              gym_id: gymTableId,
              home_gym_id: gymTableId,
              home_gym_name: gymDisplayName,
            })
            .eq('id', user.id);
          if (gymUpdateError) log.warn('Profile gym update failed:', gymUpdateError);
        }
        if (gymTableId) {
          try {
            await supabase.from('gym_memberships').upsert(
              { gym_id: gymTableId, user_id: user.id, role: 'member', status: 'active' },
              { onConflict: 'gym_id,user_id' },
            );
          } catch {}
        }
        await refreshProfile?.();
      }
    } catch (err) {
      log.warn('Failed to save gym selection', err);
      setError('Could not save gym — you can set it later in Settings.');
      setSaving(false);
      return;
    }
    setSaving(false);
    router.replace('/(onboarding)/handle');
  };

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <TouchableOpacity style={[styles.backBtn, { top: insets.top + 8 }]} onPress={() => router.back()}>
        <ChevronLeft size={24} color={c.text} />
      </TouchableOpacity>

      {/* State picker modal (native) */}
      <Modal visible={stateModalOpen} animationType="slide" transparent>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
          <View style={{ backgroundColor: c.background, borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '70%' }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: c.border }}>
              <Text style={{ color: c.text, fontSize: 18, fontFamily: TYPO.heading.bold }}>Select State</Text>
              <TouchableOpacity onPress={() => setStateModalOpen(false)} hitSlop={8}>
                <X size={22} color={c.text} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={US_STATES}
              keyExtractor={item => item.abbr}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={{ paddingVertical: 14, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: c.border }}
                  onPress={() => { setSelectedState(item.abbr); setStateModalOpen(false); setSelectedCity(null); setCityQuery(''); setGymQuery(''); setCityResults([]); setGymResults([]); }}
                >
                  <Text style={{ color: c.text, fontSize: 16 }}><Text style={{ fontWeight: '700' }}>{item.abbr}</Text>  {item.name}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={styles.inner}>
          <OnboardingProgress step={1} />
          <MapPin size={36} color={c.accent} />
          <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>Join Your Gym</Text>
          <Text style={[styles.subtitle, { color: c.textSecondary }]}>
            Find your gym by state and city, or enter a gym code.
          </Text>

          {selectedGym ? (
            <View style={[styles.selectedCard, { backgroundColor: c.surface, borderColor: c.accent }]}>
              <View style={styles.selectedRow}>
                <CheckCircle size={20} color={c.accent} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.selectedName, { color: c.text, fontFamily: TYPO.body.semibold }]}>{selectedGym.name}</Text>
                  {selectedGym.address && !selectedGym.isHomeGym && (
                    <Text style={{ color: c.textSecondary, fontSize: 13, marginTop: 2 }}>{selectedGym.address}</Text>
                  )}
                  {selectedGym.city && selectedGym.state && (
                    <Text style={{ color: c.textTertiary, fontSize: 12, marginTop: 1 }}>{selectedGym.city}, {selectedGym.state}</Text>
                  )}
                </View>
                <TouchableOpacity onPress={clearAll} hitSlop={8}>
                  <X size={18} color={c.textTertiary} />
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <>
              {/* Tab toggle */}
              <View style={{ flexDirection: 'row', borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: c.border, width: '100%' }}>
                <TouchableOpacity
                  style={{ flex: 1, paddingVertical: 10, alignItems: 'center', backgroundColor: searchMode === 'search' ? c.accent : c.surface }}
                  onPress={() => { setSearchMode('search'); setError(''); }}
                >
                  <Text style={{ color: searchMode === 'search' ? '#000' : c.textSecondary, fontWeight: '700', fontSize: 13, letterSpacing: 1 }}>GYM SEARCH</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{ flex: 1, paddingVertical: 10, alignItems: 'center', backgroundColor: searchMode === 'code' ? c.accent : c.surface }}
                  onPress={() => { setSearchMode('code'); }}
                >
                  <Text style={{ color: searchMode === 'code' ? '#000' : c.textSecondary, fontWeight: '700', fontSize: 13, letterSpacing: 1 }}>GYM CODE</Text>
                </TouchableOpacity>
              </View>

              {searchMode === 'search' ? (
                <View style={styles.codeSection}>
                  {/* Step 1: State */}
                  <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>State</Text>
                  {selectedState ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <View style={{ backgroundColor: c.accent, borderRadius: 4, paddingHorizontal: 10, paddingVertical: 6, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Text style={{ color: '#000', fontWeight: '700', fontSize: 14 }}>{selectedState}</Text>
                        <TouchableOpacity onPress={() => { setSelectedState(null); setSelectedCity(null); setCityQuery(''); setGymQuery(''); setCityResults([]); setGymResults([]); }} hitSlop={8}>
                          <X size={14} color="#000" />
                        </TouchableOpacity>
                      </View>
                      <Text style={{ color: c.textTertiary, fontSize: 13 }}>{US_STATES.find(s => s.abbr === selectedState)?.name}</Text>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={[styles.codeRow, { borderColor: c.border, backgroundColor: c.surface, justifyContent: 'space-between' }]}
                      onPress={() => setStateModalOpen(true)}
                    >
                      <Text style={{ color: c.textTertiary, fontSize: 14, paddingLeft: 10 }}>Select a state…</Text>
                      <ChevronDown size={18} color={c.textTertiary} style={{ marginRight: 8 }} />
                    </TouchableOpacity>
                  )}

                  {/* Step 2: City */}
                  {selectedState && (
                    <>
                      <Text style={[styles.sectionLabel, { color: c.textSecondary, marginTop: 8 }]}>City</Text>
                      {selectedCity ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <View style={{ backgroundColor: c.accent, borderRadius: 4, paddingHorizontal: 10, paddingVertical: 6, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                            <Text style={{ color: '#000', fontWeight: '700', fontSize: 14 }}>{selectedCity}</Text>
                            <TouchableOpacity onPress={() => { setSelectedCity(null); setCityQuery(''); setGymQuery(''); setGymResults([]); }} hitSlop={8}>
                              <X size={14} color="#000" />
                            </TouchableOpacity>
                          </View>
                        </View>
                      ) : (
                        <>
                          <View style={[styles.codeRow, { borderColor: c.border, backgroundColor: c.surface }]}>
                            <MapPin size={18} color={c.textTertiary} />
                            <TextInput
                              style={[styles.codeInput, { color: c.text, letterSpacing: 0, fontWeight: '500', fontSize: 14 }]}
                              value={cityQuery}
                              onChangeText={setCityQuery}
                              placeholder="Start typing a city…"
                              placeholderTextColor={c.textTertiary}
                              autoCorrect={false}
                              returnKeyType="next"
                            />
                            {cityLoading && <ActivityIndicator size="small" color={c.accent} />}
                          </View>
                          {cityResults.length > 0 && (
                            <ScrollView style={{ maxHeight: 160, borderWidth: 1, borderColor: c.border, borderRadius: 8, backgroundColor: c.surface, marginTop: 4 }} nestedScrollEnabled>
                              {cityResults.map(city => (
                                <TouchableOpacity
                                  key={city}
                                  style={{ paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: c.border }}
                                  onPress={() => { setSelectedCity(city); setCityQuery(''); setCityResults([]); }}
                                >
                                  <Text style={{ color: c.text, fontSize: 14 }}>{city}</Text>
                                </TouchableOpacity>
                              ))}
                            </ScrollView>
                          )}
                          {cityQuery.length >= 2 && !cityLoading && cityResults.length === 0 && (
                            <Text style={[styles.hint, { color: c.textTertiary }]}>No cities found for {selectedState}.</Text>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Step 3: Gym name */}
                  {selectedState && (
                    <>
                      <Text style={[styles.sectionLabel, { color: c.textSecondary, marginTop: 8 }]}>Gym Name</Text>
                      <View style={[styles.codeRow, { borderColor: c.border, backgroundColor: c.surface }]}>
                        <Hash size={18} color={c.textTertiary} />
                        <TextInput
                          style={[styles.codeInput, { color: c.text, letterSpacing: 0, fontWeight: '500', fontSize: 16 }]}
                          value={gymQuery}
                          onChangeText={setGymQuery}
                          placeholder={selectedCity ? `Search gyms in ${selectedCity}…` : `Search gyms in ${selectedState}…`}
                          placeholderTextColor={c.textTertiary}
                          autoCorrect={false}
                          returnKeyType="search"
                        />
                        {gymLoading && <ActivityIndicator size="small" color={c.accent} />}
                      </View>
                      {gymResults.length > 0 && (
                        <ScrollView style={{ maxHeight: 220, borderWidth: 1, borderColor: c.border, borderRadius: 8, backgroundColor: c.surface, marginTop: 4 }} nestedScrollEnabled>
                          {gymResults.map(gym => (
                            <TouchableOpacity
                              key={gym.id}
                              style={{ paddingVertical: 12, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: c.border }}
                              onPress={() => {
                                setSelectedGym({ id: gym.id, name: gym.name, address: gym.address, city: gym.city, state: gym.state });
                                setGymResults([]);
                                setGymQuery('');
                              }}
                            >
                              <Text style={{ color: c.text, fontSize: 15, fontWeight: '600' }}>{gym.name}</Text>
                              {gym.address ? <Text style={{ color: c.textSecondary, fontSize: 12, marginTop: 2 }}>{gym.address}</Text> : null}
                              <Text style={{ color: c.textTertiary, fontSize: 11, marginTop: 1 }}>{gym.city}, {gym.state}</Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      )}
                      {(selectedCity || gymQuery.length >= 2) && !gymLoading && gymResults.length === 0 && selectedState && (
                        <Text style={[styles.hint, { color: c.textTertiary }]}>No gyms found. Try a different search or use a gym code.</Text>
                      )}
                    </>
                  )}
                </View>
              ) : (
                <View style={styles.codeSection}>
                  <View style={[styles.codeRow, { borderColor: error ? (c.error || '#ff4444') : c.border, backgroundColor: c.surface }]}>
                    <Hash size={18} color={c.textTertiary} />
                    <TextInput
                      style={[styles.codeInput, { color: c.text }]}
                      value={gymCode}
                      onChangeText={(t) => { setGymCode(t.toUpperCase()); setError(''); }}
                      placeholder="e.g. PLN-FRISCO-TX-001"
                      placeholderTextColor={c.textTertiary}
                      autoCapitalize="characters"
                      autoCorrect={false}
                      returnKeyType="go"
                      onSubmitEditing={lookupGymCode}
                    />
                    {lookupLoading ? (
                      <ActivityIndicator size="small" color={c.accent} />
                    ) : (
                      gymCode.trim().length >= 3 && (
                        <TouchableOpacity
                          style={[styles.lookupBtn, { backgroundColor: c.accent }]}
                          onPress={lookupGymCode}>
                          <Text style={[styles.lookupBtnText, { color: c.accentForeground }]}>Join</Text>
                        </TouchableOpacity>
                      )
                    )}
                  </View>
                  {error ? (
                    <Text style={[styles.errorText, { color: c.error || '#ff4444' }]}>{error}</Text>
                  ) : (
                    <Text style={[styles.hint, { color: c.textTertiary }]}>Ask your gym for their DUEL code, or search by name instead.</Text>
                  )}
                </View>
              )}

              {/* Divider */}
              <View style={styles.dividerRow}>
                <View style={[styles.dividerLine, { backgroundColor: c.border }]} />
                <Text style={[styles.dividerText, { color: c.textTertiary }]}>OR</Text>
                <View style={[styles.dividerLine, { backgroundColor: c.border }]} />
              </View>

              {/* Home Gym */}
              <TouchableOpacity
                style={[styles.homeGymBtn, { backgroundColor: c.surface, borderColor: c.border }]}
                onPress={() => { setSelectedGym({ id: 'home_gym', name: 'My Home Gym', isHomeGym: true }); setError(''); }}
              >
                <Home size={22} color={c.accent} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.homeGymTitle, { color: c.text, fontFamily: TYPO.body.semibold }]}>I Lift at Home</Text>
                  <Text style={{ color: c.textSecondary, fontSize: 13 }}>Create a personal home gym</Text>
                </View>
              </TouchableOpacity>
            </>
          )}
        </View>

        <View style={styles.bottom}>
          {selectedGym ? (
            <TouchableOpacity
              style={[styles.primaryBtn, { backgroundColor: c.accent, opacity: saving ? 0.6 : 1 }]}
              onPress={saveGymAndContinue}
              disabled={saving}>
              <Text style={[styles.primaryBtnText, { color: c.accentForeground, fontFamily: TYPO.heading.bold }]}>
                {saving ? 'Setting up…' : 'CONTINUE'}
              </Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              onPress={() => router.replace('/(onboarding)/handle')}
              disabled={saving}
              style={{ paddingVertical: 12 }}>
              <Text style={{ color: c.textSecondary, fontSize: 15, textDecorationLine: 'underline' }}>Skip for now</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: { position: 'absolute', top: 50, left: 16, zIndex: 10, padding: 8 },
  scrollContent: { flexGrow: 1 },
  inner: { flex: 1, paddingHorizontal: 24, paddingTop: 80, alignItems: 'center', gap: 16 },
  title: { fontSize: 28, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 },
  subtitle: { fontSize: 15, textAlign: 'center', lineHeight: 22, maxWidth: 340, marginBottom: 8 },
  codeSection: { width: '100%', gap: 6 },
  sectionLabel: { fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase' },
  codeRow: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: 8, padding: 4, gap: 8, height: 52 },
  codeInput: { flex: 1, fontSize: 18, fontWeight: '700', letterSpacing: 2, padding: 0, paddingLeft: 14 },
  lookupBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 6 },
  lookupBtnText: { fontSize: 14, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 1 },
  hint: { fontSize: 12 },
  errorText: { fontSize: 13 },
  dividerRow: { flexDirection: 'row', alignItems: 'center', gap: 16, width: '100%', paddingVertical: 4 },
  dividerLine: { flex: 1, height: 1 },
  dividerText: { fontSize: 13, fontWeight: '700', letterSpacing: 1 },
  homeGymBtn: { flexDirection: 'row', alignItems: 'center', gap: 14, borderWidth: 1, borderRadius: 2, padding: 16, width: '100%' },
  homeGymTitle: { fontSize: 16 },
  selectedCard: { borderWidth: 2, borderRadius: 2, padding: 16, width: '100%' },
  selectedRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  selectedName: { fontSize: 16 },
  bottom: { paddingHorizontal: 24, paddingBottom: 40, alignItems: 'center' },
  primaryBtn: { width: '100%', paddingVertical: 18, borderRadius: 0, alignItems: 'center' },
  primaryBtnText: { fontSize: 18, textTransform: 'uppercase', letterSpacing: 1 },
});
